import React from 'react'
import { Link } from 'react-router-dom'
import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { ProductImage } from '../ProductImage'
import { formatPrice } from '../../utils'
import { buildModelUrl } from '../../utils/modelUrl'

/**
 * Wyniki kreatora doboru w formie tabeli porównawczej. WSPÓLNE dla wszystkich
 * trzech kategorii: falowników, modułów i magazynów.
 *
 * ═══ DLACZEGO TABELA, A NIE TRZY KARTY OBOK SIEBIE ═══
 * Karty pokazują trzy produkty, ale nie pokazują RÓŻNIC: żeby porównać jedną
 * cechę, trzeba przeskakiwać wzrokiem po trzech blokach tekstu. Wiersz ustawia
 * te same wartości w jednej linii i różnicę widać od razu.
 *
 * ═══ OCENA DOPASOWANIA ═══
 * Liczona na backendzie jako procent MAKSIMUM MOŻLIWEGO przy podanych
 * kryteriach, a nie względem najlepszego kandydata. Gdyby liczyć względem
 * najlepszego, pierwszy zawsze miałby 100% i liczba nie niosłaby informacji.
 *
 * ═══ CO KATEGORIA WNOSI OD SIEBIE ═══
 * `wiersze` to lista `[etykieta, funkcja]` z parametrami z katalogu, a
 * `wierszeBadawcze` to pary `[pole, etykieta]` z danych badawczych (dziś tylko
 * falowniki). Reszta ekranu jest identyczna, bo pytanie użytkownika jest to samo:
 * czym te trzy rzeczy się różnią.
 */

const OPIS_OCENY = (v) => (v >= 85 ? 'Bardzo dobre' : v >= 65 ? 'Dobre' : 'Częściowe')

const Kolo = ({ wartosc }) => {
  const obwod = 2 * Math.PI * 20
  const wypelnienie = (Math.max(0, Math.min(100, wartosc)) / 100) * obwod
  const kolor = wartosc >= 85 ? '#059669' : wartosc >= 65 ? '#2563eb' : '#d97706'
  return (
    <span className="inline-flex items-center gap-2.5">
      <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true" className="flex-shrink-0">
        <circle cx="24" cy="24" r="20" fill="none" stroke="#e5e7eb" strokeWidth="4" />
        <circle
          cx="24" cy="24" r="20" fill="none" stroke={kolor} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={`${wypelnienie} ${obwod}`} transform="rotate(-90 24 24)"
        />
        <text x="24" y="28" textAnchor="middle" fontSize="13" fontWeight="700" fill={kolor}>
          {wartosc}
        </text>
      </svg>
      <span className="text-sm font-semibold" style={{ color: kolor }}>{OPIS_OCENY(wartosc)}</span>
    </span>
  )
}

const wartoscAtrybutu = (p, pole) => (p.atrybuty || []).find((a) => a.pole === pole) || null

/**
 * Ikona źródła w prawym dolnym rogu komórki.
 *
 * Źródło jest częścią danej i musi być dostępne, ale wypisane pod każdą wartością
 * zajmowało więcej miejsca niż sama wartość. Ikona trzyma je pod ręką, nie
 * odbierając miejsca treści.
 */
const IkonaZrodla = ({ atrybut, pelnaWartosc }) => {
  const opis = [
    pelnaWartosc && pelnaWartosc !== atrybut.wartosc ? pelnaWartosc : null,
    atrybut.poziom_zrodla ? `źródło: ${atrybut.poziom_zrodla.replace(/_/g, ' ')}` : null,
    atrybut.zakres === 'producent' ? 'dana o marce, nie o serii' : null,
    atrybut.data_dostepu ? `sprawdzone ${atrybut.data_dostepu}` : null,
    atrybut.do_potwierdzenia ? 'do potwierdzenia u producenta' : null,
  ].filter(Boolean).join('\n')

  const klasy = 'absolute bottom-1.5 right-1.5 text-gray-300 hover:text-blue-600'
  if (!atrybut.zrodlo_url) {
    return (
      <span title={opis} aria-label={opis} className={klasy}>
        <InformationCircleIcon className="w-4 h-4" />
      </span>
    )
  }
  return (
    <a href={atrybut.zrodlo_url} target="_blank" rel="noopener noreferrer"
       title={opis} aria-label={opis} className={klasy}>
      <InformationCircleIcon className="w-4 h-4" />
    </a>
  )
}

/**
 * Kontrola elektryczna z falownikiem — osobny wiersz tabeli przy rozbudowie.
 *
 * Statusy mają WŁASNE kolory i własne słowa, bo różnica między nimi jest
 * ostra: „uwaga" znaczy „zadziała gorzej", a „błąd" znaczy „tak nie wolno,
 * uszkodzisz falownik". Zlanie ich w jeden żółty trójkąt kosztowałoby czyjś
 * sprzęt.
 */
const STATUS = {
  ok: { klasa: 'text-emerald-700', Ikona: CheckIcon },
  uwaga: { klasa: 'text-amber-700', Ikona: ExclamationTriangleIcon },
  blad: { klasa: 'text-red-700', Ikona: ExclamationTriangleIcon },
  nie_wiemy: { klasa: 'text-gray-400', Ikona: InformationCircleIcon },
}

const Kontrola = ({ pozycje }) => {
  if (!pozycje?.length) return <span className="text-gray-300">nie sprawdzaliśmy</span>
  return (
    <ul className="space-y-1.5">
      {pozycje.map((k) => {
        const { klasa, Ikona } = STATUS[k.status] || STATUS.nie_wiemy
        return (
          <li key={k.nazwa} className={`flex items-start gap-1.5 text-xs leading-4 ${klasa}`}>
            <Ikona className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-semibold">{k.nazwa}.</span> {k.opis}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

const WynikiDoboru = ({
  wynik, opisyKryteriow, onZmienKryteria,
  kategoria = 'inwerter',
  wiersze = [],
  wierszeBadawcze = [],
  skroty = {},
  cenaEtykieta = 'Cena od',
  cta = (p) => `Zobacz ${p.ofert} ofert`,
  stopka = null,
  // `onUzyj(propozycja)`: asystent w oknie nad Projektem 3D (uwaga F7, 2026-10-25): główny przycisk przenosi model do
  // projektu, a oferty otwierają się w nowej karcie, żeby nie zamykać modelu.
  onUzyj = null,
  tekstUzyj = 'Użyj w projekcie',
}) => {
  const props = wynik.propozycje
  const cenaMin = Math.min(...props.map((p) => p.cena_od))
  const badawczeDoPokazania = wierszeBadawcze.filter(
    ([pole]) => props.some((p) => wartoscAtrybutu(p, pole)),
  )

  const komorkaAtrybutu = (p, pole) => {
    const a = wartoscAtrybutu(p, pole)
    if (!a) return <span className="text-gray-300">brak danych</span>
    const skrot = skroty[pole]
    return (
      <>
        <span className="text-gray-700 pr-5 block">{skrot ? skrot(a.wartosc) : a.wartosc}</span>
        <IkonaZrodla atrybut={a} pelnaWartosc={skrot ? a.wartosc : null} />
      </>
    )
  }

  return (
    <>
      {/* Kryteria jako POZIOMY pasek nad tabelą. Pionowa kolumna po lewej zjadała
          szerokość i trzecia propozycja urywała się poza ekranem. */}
      <div className="mb-4 rounded-2xl border border-gray-200 bg-white shadow-sm px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">
            Twoje kryteria
          </span>
          {opisyKryteriow.map(({ etykieta, wartosc }) => (
            <span key={etykieta} className="text-xs leading-tight">
              <span className="block text-gray-400">{etykieta}</span>
              <span className="block font-semibold text-gray-800">{wartosc}</span>
            </span>
          ))}
          <button type="button" onClick={onZmienKryteria}
                  className="ml-auto text-xs font-semibold text-blue-600 hover:text-blue-700">
            Zmień odpowiedzi
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-2xl bg-white shadow-sm">
        <table className="table-fixed w-full border-collapse min-w-[760px]">
          <colgroup>
            <col className="w-48" />
            {props.map((p) => <col key={`col-${p.model_id}`} />)}
          </colgroup>

          <thead>
            <tr>
              <th className="bg-gray-50 border-b border-gray-100" />
              {props.map((p, i) => (
                <th key={p.model_id}
                    className={`align-top px-4 pt-0 pb-4 border-b border-l border-gray-100 ${
                      i === 0 ? 'bg-blue-50/40' : ''
                    }`}>
                  <p className={`-mx-4 mb-3 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide ${
                    i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-400'
                  }`}>
                    {i === 0 ? 'Najlepiej dopasowany' : `Propozycja ${i + 1}`}
                  </p>
                  <div className="flex flex-col items-center text-center gap-2">
                    <div className="h-24 w-full flex items-center justify-center">
                      <ProductImage src={p.img_url} alt={p.nazwa} productType={kategoria}
                                    className="max-h-full max-w-full object-contain" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      {p.producent}{p.seria ? ` · ${p.seria}` : ''}
                    </span>
                    <Link to={buildModelUrl(kategoria, { id: p.model_id })}
                          className="text-sm font-semibold text-gray-900 hover:text-blue-600 leading-snug line-clamp-2">
                      {p.nazwa}
                    </Link>
                    <span className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-blue-600 tabular-nums">
                        {formatPrice(p.cena_od)} zł
                      </span>
                      {p.cena_od === cenaMin && (
                        <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold">
                          Najtaniej
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-gray-400">{cenaEtykieta}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {props[0].dopasowanie != null && (
              <tr>
                <td className="px-4 py-3 border-b border-gray-100 align-top">
                  <span className="block text-xs font-semibold text-gray-700">Ocena dopasowania</span>
                  <span className="block text-[11px] text-gray-400 mt-0.5">do Twoich kryteriów</span>
                </td>
                {props.map((p, i) => (
                  <td key={`oc-${p.model_id}`}
                      className={`px-4 py-3 border-b border-l border-gray-100 ${i === 0 ? 'bg-blue-50/40' : ''}`}>
                    <Kolo wartosc={p.dopasowanie} />
                  </td>
                ))}
              </tr>
            )}

            {wiersze.map(([etykieta, get], ri) => (
              <tr key={etykieta} className={ri % 2 ? 'bg-gray-50/40' : ''}>
                <td className="px-4 py-3 border-b border-gray-100 text-xs font-semibold text-gray-700 align-top">
                  {etykieta}
                </td>
                {props.map((p, i) => (
                  <td key={`${etykieta}-${p.model_id}`}
                      className={`px-4 py-3 border-b border-l border-gray-100 text-sm align-top ${
                        i === 0 ? 'bg-blue-50/40' : ''
                      }`}>
                    {get(p) || <span className="text-gray-300">brak danych</span>}
                  </td>
                ))}
              </tr>
            ))}

            {badawczeDoPokazania.map(([pole, etykieta], ri) => (
              <tr key={pole} className={ri % 2 ? '' : 'bg-gray-50/40'}>
                <td className="px-4 py-3 border-b border-gray-100 text-xs font-semibold text-gray-700 align-top">
                  {etykieta}
                </td>
                {props.map((p, i) => (
                  <td key={`${pole}-${p.model_id}`}
                      className={`relative px-4 py-3 border-b border-l border-gray-100 text-sm align-top break-words ${
                        i === 0 ? 'bg-blue-50/40' : ''
                      }`}>
                    {komorkaAtrybutu(p, pole)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Kontrola z falownikiem stoi NAD uzasadnieniem: „nie wolno tak
                podłączyć" unieważnia wszystkie zalety wypisane niżej. */}
            {props.some((p) => p.kontrola?.length) && (
              <tr>
                <td className="px-4 py-3 border-b border-gray-100 align-top">
                  <span className="block text-xs font-semibold text-gray-700">Kontrola z falownikiem</span>
                  <span className="block text-[11px] text-gray-400 mt-0.5">z kart katalogowych</span>
                </td>
                {props.map((p, i) => (
                  <td key={`kt-${p.model_id}`}
                      className={`px-4 py-3 border-b border-l border-gray-100 align-top ${
                        i === 0 ? 'bg-blue-50/40' : ''
                      }`}>
                    <Kontrola pozycje={p.kontrola} />
                  </td>
                ))}
              </tr>
            )}

            <tr>
              <td className="px-4 py-3 border-b border-gray-100 text-xs font-semibold text-gray-700 align-top">
                Dlaczego pasuje
              </td>
              {props.map((p, i) => (
                <td key={`why-${p.model_id}`}
                    className={`px-4 py-3 border-b border-l border-gray-100 align-top ${
                      i === 0 ? 'bg-blue-50/40' : ''
                    }`}>
                  <ul className="space-y-1">
                    {p.powody.map((r) => (
                      <li key={r} className="flex items-start gap-1.5 text-xs text-emerald-700 leading-4">
                        <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>

            {/* Zamiast „na co uważać": czego NIE WIEMY. Wady musiałyby być naszą
                opinią, a braków w danych jesteśmy pewni. */}
            {props.some((p) => p.czego_nie_wiemy?.length) && (
              <tr className="bg-gray-50/40">
                <td className="px-4 py-3 text-xs font-semibold text-gray-700 align-top">
                  Czego nie wiemy
                </td>
                {props.map((p, i) => (
                  <td key={`nw-${p.model_id}`}
                      className={`px-4 py-3 border-l border-gray-100 align-top ${
                        i === 0 ? 'bg-blue-50/40' : ''
                      }`}>
                    {p.czego_nie_wiemy?.length ? (
                      <ul className="space-y-1">
                        {p.czego_nie_wiemy.map((r) => (
                          <li key={r} className="flex items-start gap-1.5 text-xs text-amber-700 leading-4">
                            <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-xs text-gray-400">komplet danych</span>
                    )}
                  </td>
                ))}
              </tr>
            )}

            <tr>
              <td className="px-4 py-4" />
              {props.map((p, i) => (
                <td key={`cta-${p.model_id}`}
                    className={`px-4 py-4 border-l border-gray-100 ${i === 0 ? 'bg-blue-50/40' : ''}`}>
                  {onUzyj ? (
                    <>
                      <button type="button" onClick={() => onUzyj(p)}
                              className="inline-flex w-full items-center justify-center px-3 py-2 rounded-lg
                                         bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">
                        {tekstUzyj}
                      </button>
                      <Link to={buildModelUrl(kategoria, { id: p.model_id })} target="_blank" rel="noopener noreferrer"
                            className="mt-2 block text-center text-xs font-semibold text-blue-700 hover:underline">
                        {cta(p)}
                      </Link>
                    </>
                  ) : (
                    <Link to={buildModelUrl(kategoria, { id: p.model_id })}
                          className="inline-flex w-full items-center justify-center px-3 py-2 rounded-lg
                                     bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold no-underline">
                      {cta(p)}
                    </Link>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-gray-400 leading-4">
        {stopka || (
          <>
            Ocena liczy się z Twoich odpowiedzi i danych, które mamy o tych modelach. Nie ocenia
            jakości wykonania ani awaryjności, bo takich danych w katalogu nie ma.
          </>
        )}
      </p>
    </>
  )
}

export default WynikiDoboru
